import { generateThumbnail, captureVideoFrame } from './thumbnailGenerator';

const DB_NAME = 'ai-history-db';
const DB_VERSION = 1;
const META_STORE = 'historyMeta';
const BLOB_STORE = 'historyBlobs';

const openDB = () =>
  new Promise((resolve, reject) => {
    if (typeof window === 'undefined' || !window.indexedDB) {
      reject(new Error('IndexedDB не поддерживается'));
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;

      if (!db.objectStoreNames.contains(META_STORE)) {
        const metaStore = db.createObjectStore(META_STORE, { keyPath: 'id' });
        metaStore.createIndex('storeKey', 'storeKey', { unique: false });
        metaStore.createIndex('timestamp', 'timestamp', { unique: false });
      }

      if (!db.objectStoreNames.contains(BLOB_STORE)) {
        db.createObjectStore(BLOB_STORE, { keyPath: 'key' });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

/**
 * Сохранить элемент истории с блобами.
 * @param {string} storeKey — ключ компонента (например 'ai-upscale-history')
 * @param {object} metadata — { id, prompt, timestamp, ... } (без urls)
 * @param {Blob[]} fullBlobs — массив полноразмерных изображений
 * @param {Blob[]} thumbBlobs — массив миниатюр
 */
export const saveHistoryItem = async (storeKey, metadata, fullBlobs = [], thumbBlobs = []) => {
  const db = await openDB();
  const tx = db.transaction([META_STORE, BLOB_STORE], 'readwrite');
  const metaS = tx.objectStore(META_STORE);
  const blobS = tx.objectStore(BLOB_STORE);

  const meta = {
    ...metadata,
    storeKey,
    imageCount: fullBlobs.length,
    blobStatus: fullBlobs.length > 0 ? 'saved' : 'url_only',
  };

  metaS.put(meta);

  fullBlobs.forEach((blob, i) => {
    blobS.put({ key: `${meta.id}_full_${i}`, blob, size: blob.size });
  });

  thumbBlobs.forEach((blob, i) => {
    blobS.put({ key: `${meta.id}_thumb_${i}`, blob, size: blob.size });
  });

  return new Promise((resolve, reject) => {
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = () => { db.close(); reject(tx.error); };
  });
};

/**
 * Получить все метаданные для компонента, отсортированные по id (новые первые).
 */
export const getHistoryItems = async (storeKey) => {
  const db = await openDB();
  const tx = db.transaction(META_STORE, 'readonly');
  const index = tx.objectStore(META_STORE).index('storeKey');
  const request = index.getAll(storeKey);

  return new Promise((resolve, reject) => {
    request.onsuccess = () => {
      db.close();
      const items = request.result.sort((a, b) => b.id - a.id);
      resolve(items);
    };
    request.onerror = () => { db.close(); reject(request.error); };
  });
};

/**
 * Получить блоб по itemId и типу ('full' | 'thumb') и индексу.
 */
export const getBlob = async (itemId, type = 'thumb', index = 0) => {
  const db = await openDB();
  const tx = db.transaction(BLOB_STORE, 'readonly');
  const request = tx.objectStore(BLOB_STORE).get(`${itemId}_${type}_${index}`);

  return new Promise((resolve, reject) => {
    request.onsuccess = () => { db.close(); resolve(request.result?.blob || null); };
    request.onerror = () => { db.close(); reject(request.error); };
  });
};

/**
 * Получить все блобы для элемента (все full или все thumb).
 */
export const getAllBlobs = async (itemId, type = 'thumb', count = 1) => {
  const blobs = [];
  for (let i = 0; i < count; i++) {
    const blob = await getBlob(itemId, type, i);
    blobs.push(blob);
  }
  return blobs;
};

/**
 * Удалить элемент и его блобы.
 */
export const deleteHistoryItem = async (itemId, imageCount = 1) => {
  const db = await openDB();
  const tx = db.transaction([META_STORE, BLOB_STORE], 'readwrite');
  const metaS = tx.objectStore(META_STORE);
  const blobS = tx.objectStore(BLOB_STORE);

  metaS.delete(itemId);

  for (let i = 0; i < imageCount; i++) {
    blobS.delete(`${itemId}_full_${i}`);
    blobS.delete(`${itemId}_thumb_${i}`);
  }

  return new Promise((resolve, reject) => {
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = () => { db.close(); reject(tx.error); };
  });
};

/**
 * Очистить всю историю для компонента.
 */
export const clearHistory = async (storeKey) => {
  const items = await getHistoryItems(storeKey);
  for (const item of items) {
    await deleteHistoryItem(item.id, item.imageCount || 1);
  }
};

/**
 * Удалить старые записи, оставив не более maxItems.
 */
export const pruneOldest = async (storeKey, maxItems = 50) => {
  const items = await getHistoryItems(storeKey);
  if (items.length <= maxItems) return;

  const toRemove = items.slice(maxItems);
  for (const item of toRemove) {
    await deleteHistoryItem(item.id, item.imageCount || 1);
  }
};

/**
 * Скачать изображение по URL и вернуть Blob.
 * Сначала пробует напрямую, если CORS — через /api/proxy-image.
 */
export const fetchImageAsBlob = async (url) => {
  if (!url) return null;

  // base64 data URL → Blob напрямую
  if (url.startsWith('data:')) {
    const resp = await fetch(url);
    return resp.blob();
  }

  // Локальный путь — фетчим напрямую
  if (url.startsWith('/') || url.startsWith('blob:')) {
    const resp = await fetch(url);
    return resp.blob();
  }

  // Внешний URL — сначала напрямую (сохраняет оригинальный размер)
  try {
    const resp = await fetch(url, { mode: 'cors' });
    if (resp.ok) return resp.blob();
  } catch {
    // CORS ошибка — fallback на прокси
  }

  // Fallback: через прокси (Gatsby serverless может обрезать большие файлы)
  const proxyUrl = `/api/proxy-image?url=${encodeURIComponent(url)}`;
  const resp = await fetch(proxyUrl);
  if (!resp.ok) throw new Error(`Proxy fetch failed: ${resp.status}`);
  return resp.blob();
};

/**
 * Добавить элемент в историю: скачивает блобы, делает миниатюры, пишет в IndexedDB.
 * Вынесено из useImageHistory, чтобы очередь задач (utils/aiJobs) могла писать историю без React.
 * По окончании стреляет 'ai-history-changed' — открытые хуки с тем же storeKey перечитывают сетку.
 *
 * @param {string} storeKey — ключ в IndexedDB, например 'ai-upscale-history'
 * @param {object} metadata — { prompt, ... }, id и timestamp проставляются сами
 * @param {string|string[]} urls — URL(ы) изображений/видео
 */
export const addHistoryItem = async (storeKey, metadata, urls, options = {}) => {
  const {
    mediaType = 'image',
    maxItems = 50,
    storeFullBlob = mediaType === 'image',
  } = options;

  const urlArray = Array.isArray(urls) ? urls : [urls];
  const id = metadata.id || Date.now();
  const timestamp = metadata.timestamp || new Date().toLocaleString('ru-RU');

  const fullBlobs = [];
  const thumbBlobs = [];

  for (const url of urlArray) {
    try {
      if (mediaType === 'video') {
        try {
          const frame = await captureVideoFrame(url);
          thumbBlobs.push(frame);
        } catch {
          thumbBlobs.push(null);
        }
      } else {
        const blob = await fetchImageAsBlob(url);
        if (blob) {
          if (storeFullBlob) fullBlobs.push(blob);
          const thumb = await generateThumbnail(blob);
          thumbBlobs.push(thumb);
        }
      }
    } catch (err) {
      console.warn(`addHistoryItem: не удалось скачать ${url}`, err);
    }
  }

  const meta = {
    ...metadata,
    id,
    timestamp,
    originalUrls: urlArray,
  };

  try {
    await saveHistoryItem(storeKey, meta, fullBlobs.filter(Boolean), thumbBlobs.filter(Boolean));
    await pruneOldest(storeKey, maxItems);
  } catch (err) {
    if (err.name === 'QuotaExceededError') {
      await pruneOldest(storeKey, Math.floor(maxItems / 2));
      await saveHistoryItem(storeKey, meta, fullBlobs.filter(Boolean), thumbBlobs.filter(Boolean));
    } else {
      throw err;
    }
  }

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('ai-history-changed', { detail: storeKey }));
  }
};
